import React from "react";
import BaseEdge, { roundedOrthPath, roundTo } from "./BaseEdge";

/**
 * TopToBottomEdge
 *
 * MaxGraph equivalent: mxEdgeStyle.TopToBottom
 * Behavior:
 *  - Connects top of source to bottom of target (vertical priority)
 *  - Routing shape:
 *        src
 *         |
 *         cp1 (midX, src.y)
 *         |
 *         cp2 (midX, dst.y)
 *         |
 *        dst
 *
 *  - Fully supports:
 *    ✔ Dragging (C1 behavior)
 *    ✔ Hover glow
 *    ✔ Labeling
 *    ✔ Auto-generated control points (cp1, cp2)
 */

function anchor(node, port /* "top"|"bottom" */) {
  const w = node.width;
  const h = node.height;
  if (port === "top") return { x: roundTo(node.x + w / 2), y: roundTo(node.y) };
  return { x: roundTo(node.x + w / 2), y: roundTo(node.y + h) };
}

export default function TopToBottomEdge({
  edge,
  nodeById,
  edgeIndex,
  selectedIndex,
  onSelectEdge,
  onStartEdgeDrag,
  edgeControls,
  color,

  // hover props
  isHovered,
  onHoverEnter,
  onHoverLeave,
}) {
  const fromNode = nodeById[edge.from];
  const toNode = nodeById[edge.to];
  if (!fromNode || !toNode) return null;

  const fromCenterY = fromNode.y + fromNode.height / 2;
  const toCenterY = toNode.y + toNode.height / 2;

  const fromPort = toCenterY >= fromCenterY ? "bottom" : "top";
  const toPort = fromPort === "bottom" ? "top" : "bottom";

  const src = anchor(fromNode, fromPort);
  const dst = anchor(toNode, toPort);

  // mid X between nodes
  const defaultMidX = Math.round((src.x + dst.x) / 2);

  // control points from edgeControls or defaults
  const ctrl = edgeControls?.[edgeIndex];

  const cp1 = ctrl?.cp1 ?? { x: defaultMidX, y: src.y };
  const cp2 = ctrl?.cp2 ?? { x: defaultMidX, y: dst.y };

  const pts = [src, cp1, cp2, dst];
  const d = roundedOrthPath(pts);

  const labelPos = {
    x: defaultMidX,
    y: Math.round((src.y + dst.y) / 2) - 12,
  };

  return (
    <BaseEdge
      d={d}
      stroke={color}

      // hover support
      isHovered={isHovered}
      onHoverEnter={onHoverEnter}
      onHoverLeave={onHoverLeave}

      onSelect={() => onSelectEdge(edgeIndex)}
      onStartDrag={(e) =>
        onStartEdgeDrag(edgeIndex, {
          clientX: e.clientX,
          clientY: e.clientY,
        })
      }
      label={edge.label}
      labelPos={labelPos}
    />
  );
}
